import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, waLink } from "@/data/site";
import { Wordmark } from "./Wordmark";
import { cn } from "@/lib/utils";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const solid = scrolled || !isHome || open;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-500",
        solid ? "bg-[#F7F5F0]/90 backdrop-blur-md border-b border-[#E2DDD5]/70" : "bg-transparent"
      )}
      data-testid="site-header"
    >
      <div className="ss-container flex items-center justify-between py-4 lg:py-5">
        <Wordmark className="h-[12px] sm:h-[13px] lg:h-[14px]" tone={solid ? "ink" : "light"} />

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "ss-link text-[0.8rem] tracking-[0.06em] font-body transition-colors",
                  solid ? "text-[#1A1C1E]" : "text-[#F7F5F0]",
                  isActive && "opacity-60"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/advisory"
            data-testid="nav-discuss-cta"
            className={cn(
              "hidden sm:inline-flex items-center px-5 py-2.5 text-[0.72rem] uppercase tracking-[0.1em] font-medium border transition-colors duration-300",
              solid
                ? "border-[#1A1C1E] text-[#1A1C1E] hover:bg-[#1A1C1E] hover:text-[#F7F5F0]"
                : "border-[#F7F5F0]/70 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1C1E]"
            )}
          >
            Discuss a Property
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className={cn("lg:hidden p-2 -mr-2", solid ? "text-[#1A1C1E]" : "text-[#F7F5F0]")}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
            className="lg:hidden bg-[#F7F5F0] border-b border-[#E2DDD5] h-[calc(100vh-64px)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="ss-container py-8 flex flex-col gap-1" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block font-display text-3xl py-3 border-b border-[#E2DDD5]/60 text-[#1A1C1E]"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <Link
                  to="/advisory"
                  className="text-center px-6 py-4 bg-[#1A1C1E] text-[#F7F5F0] text-sm uppercase tracking-[0.1em]"
                  data-testid="mobile-discuss-cta"
                >
                  Discuss a Property
                </Link>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center px-6 py-4 border border-[#C8C2B7] text-[#1A1C1E] text-sm uppercase tracking-[0.1em]"
                  data-testid="mobile-whatsapp"
                >
                  WhatsApp us
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
